<script setup lang="ts">
import { Bell } from 'lucide-vue-next';
import { useNotifications } from '~/composables/useNotifications';

const { loggedIn } = useUserSession();
const router = useRouter();
const { t } = useI18n();

const { notifications, unreadCount, markAsRead } = useNotifications();
type NotificationItem = (typeof notifications.value)[number];

const handleNotificationClick = async (notification: NotificationItem) => {
  if (!notification.isRead) {
    await markAsRead([notification.id]);
  }
  if (notification.url) {
    useRouter().push(notification.url);
  }
};

const markAllAsRead = async () => {
  await markAsRead();
};
</script>

<template>
  <ClientOnly>
    <UPopover v-if="loggedIn" :popper="{ placement: 'bottom-end' }">
      <UChip
        :show="unreadCount > 0"
        :text="unreadCount"
        size="2xl"
        color="error"
        @click="router.push('/notifications')"
      >
        <UButton color="neutral" variant="ghost" icon="i-heroicons-bell" class="rounded-full h-7 w-7 p-1" />
      </UChip>

      <template #panel>
        <div
          class="w-80 sm:w-96 bg-white dark:bg-gray-900 shadow-xl rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
        >
          <div
            class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50"
          >
            <h3 class="font-semibold text-gray-900 dark:text-white">
              {{ t('notifications.title') || 'Notificações' }}
            </h3>
            <UButton v-if="unreadCount > 0" variant="link" size="xs" @click="markAllAsRead">
              {{ t('notifications.mark_all_read') || 'Marcar todas como lidas' }}
            </UButton>
          </div>

          <div class="max-h-96 overflow-y-auto">
            <div v-if="notifications.length === 0" class="p-8 text-center text-gray-500">
              <Bell class="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p>
                {{ t('notifications.empty') || 'Nenhuma notificação por enquanto' }}
              </p>
            </div>

            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="p-4 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
              :class="{
                'bg-primary-50/30 dark:bg-primary-900/10': !notification.isRead
              }"
              @click="handleNotificationClick(notification)"
            >
              <div class="flex gap-3">
                <div class="shrink-0 mt-1">
                  <UAvatar :src="notification.icon || '/icons/icon-192x192.png'" size="sm" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {{ notification.title }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {{ notification.body }}
                  </p>
                  <p class="text-[10px] text-gray-400 mt-1">
                    {{ new Date(notification.createdAt).toLocaleString() }}
                  </p>
                </div>
                <div v-if="!notification.isRead" class="shrink-0 self-center">
                  <div class="w-2 h-2 bg-primary-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-2 border-t border-gray-200 dark:border-gray-700 text-center bg-gray-50 dark:bg-gray-800/50">
            <UButton to="/notifications" variant="ghost" block size="sm">
              {{ t('notifications.view_all') || 'Ver todas' }}
            </UButton>
          </div>
        </div>
      </template>
    </UPopover>
  </ClientOnly>
</template>
